import React, { useState } from 'react';
import { CloseCircleGreyIcon } from 'assets';

interface Comment {
  id: string;
  author: string;
  content: string;
  timestamp: string;
}

interface CommentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  comments?: Comment[];
  onSubmitComment: (content: string) => void;
  currentUser?: string;
  isLoading?: boolean;
}

const CommentsModal: React.FC<CommentsModalProps> = ({
  isOpen,
  onClose,
  comments = [],
  onSubmitComment,
  currentUser = 'Jane Doe',
  isLoading = false,
}) => {
  const [commentText, setCommentText] = useState('');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);

  const handleSubmit = () => {
    if (!commentText.trim()) return;
    onSubmitComment(commentText);
    setCommentText('');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const commentTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - commentTime.getTime()) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} mins ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-[#E0F2FE]">
          <h2 className="text-2xl font-semibold text-gray-900">Comments</h2>
          <button
            onClick={onClose}
            className="transition-colors !bg-transparent !p-0 !w-fit !h-fit hover:opacity-70"
          >
            <CloseCircleGreyIcon className="text-[#5a5a66] w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Comment Editor */}
          <div className="border border-[#0369A1] rounded-lg p-4 mb-6">
            {/* User Info */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#BAE6FD] flex items-center justify-center text-[#0369A1] font-medium">
                {getInitials(currentUser)}
              </div>
              <span className="font-semibold text-[#0C4A6E]">{currentUser}</span>
            </div>

            {/* Text Area */}
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Write a comment..."
              className="w-full min-h-[100px] p-2 text-sm text-gray-700 placeholder-gray-400 border-none outline-none resize-none"
              style={{
                fontWeight: isBold ? 'bold' : 'normal',
                fontStyle: isItalic ? 'italic' : 'normal',
                textDecoration: isUnderline ? 'underline' : 'none',
              }}
            />

            {/* Toolbar */}
            <div className="flex sm:flex-row flex-col sm:items-center justify-between pt-3 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsBold(!isBold)}
                  className={`w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors !bg-transparent ${
                    isBold ? 'bg-gray-200' : ''
                  }`}
                  title="Bold"
                >
                  <span className="font-bold text-gray-700">B</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsItalic(!isItalic)}
                  className={`w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors !bg-transparent ${
                    isItalic ? 'bg-gray-200' : ''
                  }`}
                  title="Italic"
                >
                  <span className="italic text-gray-700">I</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsUnderline(!isUnderline)}
                  className={`w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors !bg-transparent ${
                    isUnderline ? 'bg-gray-200' : ''
                  }`}
                  title="Underline"
                >
                  <span className="underline text-gray-700">U</span>
                </button>
                <button
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors !bg-transparent"
                  title="Add link"
                >
                  <span className="underline text-gray-700">I</span>
                </button>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!commentText.trim() || isLoading}
                className="px-6 py-2 text-sm font-medium text-white !bg-[#0369A1] !ml-0 sm:mt-0 mt-4 rounded-md hover:bg-[#075985] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Posting...' : 'Comment'}
              </button>
            </div>
          </div>

          {/* Previous Comments */}
          {comments.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No previous comments</p>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.id} className="border-b border-gray-200 pb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#BAE6FD] flex items-center justify-center text-[#0369A1] font-medium flex-shrink-0">
                      {getInitials(comment.author)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-[#0C4A6E]">{comment.author}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{comment.content}</p>
                      <span className="text-xs text-gray-500">{getTimeAgo(comment.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export { CommentsModal };